import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import useLoadOptions from "@/hooks/useLoadOptions";
import CompanyCourseInstructor, {
  CompanyCourseInstructorForm,
} from "@/interfaces/CompanyCourseInstructor";
import { InstructorFilters } from "@/interfaces/Instructor";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useStoreCompanyCourseInstructorMutation } from "@/services/companies/courses/instructors";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import classNames from "classnames";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface StoreCompanyCourseInstructorModalProps
  extends IModalChildrenProps<unknown, CompanyCourseInstructor> {
  companyId: string;
  companyCourseId: string;
  courseId: string | null;
}

const StoreCompanyCourseInstructorModalRef: ForwardRefRenderFunction<
  IModalMethods,
  StoreCompanyCourseInstructorModalProps
> = ({ onHide, companyId, companyCourseId, courseId }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const { loadInstructorsOptions } = useLoadOptions();
  const defaultAdditional = useRef<InstructorFilters>({
    pageIndex: 0,
    courseId,
  }).current;
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyCourseInstructorForm>();
  const [storeCompanyCourseInstructorAsync] =
    useStoreCompanyCourseInstructorMutation();
  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CompanyCourseInstructorForm> = ({
    instructor,
    ...data
  }) => {
    toast.promise(
      storeCompanyCourseInstructorAsync([
        companyId,
        companyCourseId,
        {
          ...data,
          instructorId: instructor.id,
        },
      ]).unwrap(),
      {
        pending: "Création du formateur en cours ...",
        success: {
          render() {
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Le formateur à été créer 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<CompanyCourseInstructorForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter un formateur"
      requiredLabel
      onHide={onHide}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Formateur <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="instructor"
              rules={{ required: "Ce champ est obligatoire" }}
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadInstructorsOptions}
                  getOptionLabel={(option) => option.fullname}
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  isClearable
                  additional={defaultAdditional}
                  onChange={field.onChange}
                />
              )}
            />
            <Form.Control.Feedback
              type="invalid"
              className={classNames({
                "d-block": errors.instructor,
              })}
            >
              {errors.instructor?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Montant des honoraires du formateur{" "}
              <span className="text-danger">*</span> :
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                placeholder="ex : 350"
                {...register("priceHt", {
                  required: "Ce champ est obligatoire",
                })}
                isInvalid={!!errors.priceHt}
              />
              <InputGroup.Text>€</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.priceHt?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Date de contrat du formateur{" "}
              <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="date"
              {...register("contractDate", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.contractDate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.contractDate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCompanyCourseInstructorModalRef);
